import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useTranslation } from 'react-i18next';
import { SalesGoalsFilter } from './components/sales-goals.filter';
import { SalesGoalsTable } from './components/sales-goals.table';
import { useSalesGoals } from './hooks/sales-goals.hook';
import { GoalMonth, shiftMonth } from './sales-goals';

const currentMonth = (): GoalMonth => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

const monthFromUrl = (params: URLSearchParams): GoalMonth => {
    const year = Number(params.get('year'));
    const month = Number(params.get('month'));
    return year >= 2000 && year <= 2200 && month >= 1 && month <= 12
        ? { year, month }
        : currentMonth();
};

export const SalesGoalsPage = () => {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [month, setMonth] = useState<GoalMonth>(() => monthFromUrl(searchParams));
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [goalFilter, setGoalFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { report, drafts, loading, savingUserId, change, saveUser } = useSalesGoals(month);

    const monthLabel = new Date(month.year, month.month - 1, 1)
        .toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });

    useEffect(() => {
        setSearchParams({ year: String(month.year), month: String(month.month) }, { replace: true });
        setPage(0);
    }, [month, setSearchParams]);

    const filteredUsers = useMemo(() => (report?.users ?? []).filter((user) => {
        const matchesName = user.name.toLocaleLowerCase().includes(appliedSearch.trim().toLocaleLowerCase());
        const hasGoal = user.revenueTarget != null || user.salesTarget != null;
        const matchesGoal = goalFilter === 'all' || (goalFilter === 'with' ? hasGoal : !hasGoal);
        return matchesName && matchesGoal;
    }), [appliedSearch, goalFilter, report]);

    const clearFilters = () => {
        setSearch('');
        setAppliedSearch('');
        setGoalFilter('all');
        setPage(0);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('metas.titulo')} />
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" aria-label={t('metas.mesAnterior')} onClick={() => setMonth((old) => shiftMonth(old, -1))}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={{ color: 'text.primary', fontWeight: 600, minWidth: 140, textAlign: 'center', '&::first-letter': { textTransform: 'uppercase' } }}>
                        {monthLabel}
                    </Typography>
                    <IconButton size="small" aria-label={t('metas.proximoMes')} onClick={() => setMonth((old) => shiftMonth(old, 1))}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mb: 1.5 }}>
                {t('metas.dialogoAjuda')}
            </Typography>
            <Grid container spacing={1} alignItems="center">
                <SalesGoalsFilter
                    search={search}
                    setSearch={setSearch}
                    goalFilter={goalFilter}
                    onGoalFilter={(value) => { setGoalFilter(value); setPage(0); }}
                    onSearch={() => { setAppliedSearch(search); setPage(0); }}
                    onClear={clearFilters}
                />
            </Grid>
            <SalesGoalsTable
                users={filteredUsers}
                drafts={drafts}
                loading={loading}
                emptyMessage={report?.users.length ? t('metas.nenhumResultado') : t('metas.semVendedores')}
                page={page}
                rowsPerPage={rowsPerPage}
                onChange={change}
                onPageChange={setPage}
                onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(0); }}
                savingUserId={savingUserId}
                onSave={(userId) => saveUser(userId)}
                onRemove={(userId) => saveUser(userId, true)}
            />
        </DashboardLayout>
    );
};
